import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getProfile, getAdminUserFullProfile, editAdminUserProfile, updateAdminUser,
  getAdminReferrals, editReferral, resolveReferral,
  getAdminCommissions, markCommissionPaid, markCommissionUnpaid,
} from '../api/api';
import {
  Avatar, StatTile, StatusBadge, ReferralDetailModal, folio,
  EVENT_TYPE_LABELS, EVENT_MODALITY_LABELS, formatEventDate, SkeletonPage,
  USER_STATUS_LABELS, USER_ROLE_LABELS,
} from '../components/Charts';
import { uiForm, uiConfirm, uiAlert, uiMoneyPrompt } from '../components/Modal';
import { uiToastSuccess, uiToastError } from '../components/Toast';

const INVITADO_STATUS = {
  pendiente: { label: 'Pendiente', tone: 'warning' },
  aprobado: { label: 'Aprobado', tone: 'success' },
  rechazado: { label: 'Rechazado', tone: 'danger' },
};

const money = (n) => `$${parseFloat(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const parseMoney = (val) => parseFloat(String(val).replace(/,/g, '').replace(/\$/g, '').trim());

function StatusPill({ children, tone = 'neutral' }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

// Vista de perfil de un miembro para administración — página propia, no modal, con
// absolutamente toda la info que le sirve a admin sobre esa persona: datos de cuenta,
// servicios que ofrece, asistencia, invitados que ha llevado, y las dos caras de sus
// referencias (las que envió como referrer y las que recibió como empresa), con
// comisiones y el mismo panel de acciones que ya existe en Referencias/Comisiones.
function AdminMemberProfile() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const id = parseInt(userId, 10);

  const [checking, setChecking] = useState(true);
  const [myRole, setMyRole] = useState(null);
  const [myId, setMyId] = useState(null);
  const [approveRole, setApproveRole] = useState('member');
  const [data, setData] = useState(null); // { user, attendance, invitados, services }
  const [referralsSent, setReferralsSent] = useState([]);
  const [referralsReceived, setReferralsReceived] = useState([]);
  const [commissionsEarned, setCommissionsEarned] = useState([]);
  const [allCommissions, setAllCommissions] = useState([]);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, referralsRes, commissionsRes] = await Promise.all([
        getAdminUserFullProfile(id),
        getAdminReferrals(),
        getAdminCommissions(),
      ]);
      setData(profileRes.data);
      setReferralsSent(referralsRes.data.filter(r => r.referrer_id === id));
      setReferralsReceived(referralsRes.data.filter(r => r.provider_id === id));
      setAllCommissions(commissionsRes.data);
      const sentIds = new Set(referralsRes.data.filter(r => r.referrer_id === id).map(r => r.id));
      setCommissionsEarned(commissionsRes.data.filter(c => sentIds.has(c.referral_id)));
    } catch (err) {
      uiToastError('No se pudo cargar el perfil');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    getProfile()
      .then(res => {
        if (!['admin', 'owner'].includes(res.data.role)) { navigate('/dashboard'); return; }
        setMyRole(res.data.role);
        setMyId(res.data.id);
        setChecking(false);
        loadAll();
      })
      .catch(() => navigate('/'));
  }, [navigate, loadAll]);

  // Polling — refresca este perfil cada 25s, igual que el resto del panel de Admin.
  useEffect(() => {
    const interval = setInterval(loadAll, 25000);
    return () => clearInterval(interval);
  }, [loadAll]);

  const ROLE_LABELS = { member: 'Miembro (negocio)', admin: 'Administrador', owner: 'Dueño' };

  const approveWithRole = async () => {
    if (!(await uiConfirm(`¿Aprobar a ${data.user.name} como ${ROLE_LABELS[approveRole]}?`))) return;
    try {
      await updateAdminUser(id, { status: 'active', role: approveRole });
      uiToastSuccess('Usuario aprobado.');
      loadAll();
    } catch (err) {
      uiToastError('Error aprobando usuario');
    }
  };

  const updateUser = async (changes, confirmMessage) => {
    if (confirmMessage && !(await uiConfirm(confirmMessage))) return;
    try {
      await updateAdminUser(id, changes);
      uiToastSuccess('Usuario actualizado.');
      loadAll();
    } catch (err) {
      uiToastError('Error actualizando usuario');
    }
  };

  const editProfile = async () => {
    const result = await uiForm(`Editar perfil — ${data.user.name}`, [
      { name: 'name', label: 'Nombre', type: 'text', value: data.user.name },
      { name: 'business_name', label: 'Negocio', type: 'text', value: data.user.business_name || '' },
      { name: 'phone', label: 'Teléfono', type: 'text', value: data.user.phone || '' },
      { name: 'email', label: 'Email', type: 'text', value: data.user.email },
      { name: 'dues_paid_until', label: 'Anualidad pagada hasta', type: 'date', value: (data.user.dues_paid_until || '').slice(0, 10) },
    ]);
    if (!result) return;
    try {
      await editAdminUserProfile(id, result);
      uiToastSuccess('Perfil actualizado.');
      loadAll();
    } catch (err) {
      uiToastError('Error actualizando el perfil');
    }
  };

  // Mismas acciones que ya viven en AdminUsers.js para una referencia (editar, resolver,
  // marcar pagada/no pagada) — duplicadas aquí porque esta es una página independiente,
  // no comparte estado en memoria con el panel de Admin.
  const resolveRef = async (refId, status) => {
    let deal_amount = null;
    if (status === 'confirmed_deal') {
      const val = await uiMoneyPrompt('¿Monto final del trato (sin IVA)?');
      if (!val) return;
      deal_amount = parseMoney(val);
      if (isNaN(deal_amount) || deal_amount <= 0) return uiAlert('Monto inválido.');
    }
    try {
      await resolveReferral(refId, { status, deal_amount });
      uiToastSuccess('Referencia resuelta.');
      loadAll();
    } catch (err) {
      uiToastError('Error resolviendo referencia');
    }
  };

  const editRef = async (r) => {
    const result = await uiForm(`Editar referencia ${folio(r.id)}`, [
      { name: 'prospect_name', label: 'Nombre del prospecto', type: 'text', value: r.prospect_name },
      { name: 'prospect_phone', label: 'Teléfono del prospecto', type: 'text', value: r.prospect_phone || '' },
      { name: 'deal_amount', label: 'Monto del trato sin IVA (déjalo igual si no aplica)', type: 'money', value: r.deal_amount || '' },
    ]);
    if (!result) return;
    let newAmount = null;
    if (result.deal_amount !== undefined && result.deal_amount !== '') {
      const parsed = parseMoney(result.deal_amount);
      if (isNaN(parsed) || parsed <= 0) return uiAlert('Monto inválido.');
      newAmount = parsed;
    }
    try {
      await editReferral(r.id, { prospect_name: result.prospect_name, prospect_phone: result.prospect_phone, deal_amount: newAmount });
      uiToastSuccess('Referencia actualizada.');
      loadAll();
    } catch (err) {
      uiToastError('Error editando la referencia');
    }
  };

  const markPaid = async (commissionId) => {
    try {
      await markCommissionPaid(commissionId);
      uiToastSuccess('Comisión marcada como pagada.');
      loadAll();
    } catch (err) {
      uiToastError('Error marcando como pagada');
    }
  };

  const markUnpaid = async (c) => {
    if (!(await uiConfirm(`¿Marcar como NO pagada la comisión de ${c.prospect_name}?`))) return;
    try {
      await markCommissionUnpaid(c.id);
      uiToastSuccess('Comisión marcada como no pagada.');
      loadAll();
    } catch (err) {
      uiToastError('Error revirtiendo el pago');
    }
  };

  const adminReferralActions = (r) => {
    if (!r) return [];
    const actions = [
      { label: 'Editar', variant: 'secondary', onClick: () => { setSelectedReferral(null); editRef(r); } },
    ];
    if (r.status !== 'confirmed_deal' && r.status !== 'paid') {
      actions.push({ label: 'Confirmar: sí hubo negocio', variant: 'primary', onClick: () => { setSelectedReferral(null); resolveRef(r.id, 'confirmed_deal'); } });
    }
    if (r.status !== 'confirmed_no_deal') {
      actions.push({ label: 'Confirmar: no hubo negocio', variant: 'secondary', onClick: () => { setSelectedReferral(null); resolveRef(r.id, 'confirmed_no_deal'); } });
    }
    if (r.status !== 'rejected_duplicate') {
      actions.push({ label: 'Rechazar como duplicada', variant: 'secondary', onClick: () => { setSelectedReferral(null); resolveRef(r.id, 'rejected_duplicate'); } });
    }
    const commission = allCommissions.find(c => c.referral_id === r.id);
    if (commission && !commission.paid) {
      actions.push({ label: 'Marcar comisión pagada', variant: 'primary', onClick: () => { setSelectedReferral(null); markPaid(commission.id); } });
    }
    if (commission && commission.paid) {
      actions.push({ label: 'Marcar comisión no pagada', variant: 'danger', onClick: () => { setSelectedReferral(null); markUnpaid(commission); } });
    }
    return actions;
  };

  if (checking || loading || !data) return <div className="page"><SkeletonPage /></div>;

  const { user, attendance, invitados, services } = data;
  const dealsClosed = referralsSent.filter(r => r.status === 'confirmed_deal' || r.status === 'paid').length;
  const totalEarned = commissionsEarned.reduce((sum, c) => sum + parseFloat(c.member_commission || 0), 0);
  const totalPaid = commissionsEarned.filter(c => c.paid).reduce((sum, c) => sum + parseFloat(c.member_commission || 0), 0);
  const totalPending = totalEarned - totalPaid;

  return (
    <div className="page">
      <button className="link-button" onClick={() => navigate('/admin/usuarios')} style={{ marginBottom: '16px' }}>← Volver a usuarios</button>

      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
        <Avatar src={user.avatar_url} seed={user.name} size={72} />
        <div style={{ flex: 1, minWidth: '220px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: '22px', margin: 0 }}>{user.name}</h1>
            <StatusPill tone={user.status === 'active' ? 'success' : user.status === 'rejected' ? 'danger' : user.status === 'inactive' ? 'neutral' : 'warning'}>{USER_STATUS_LABELS[user.status] || user.status}</StatusPill>
            <StatusPill tone={user.role === 'owner' ? 'success' : user.role === 'admin' ? 'info' : 'neutral'}>{USER_ROLE_LABELS[user.role] || user.role}</StatusPill>
          </div>
          <p style={{ margin: '4px 0 0', color: 'var(--neutral-500)', fontSize: '14px' }}>
            {user.business_name || 'Sin negocio registrado'} · {user.email}{user.phone ? ` · ${user.phone}` : ''}
          </p>
          <p style={{ margin: '4px 0 0', color: 'var(--neutral-500)', fontSize: '13px' }}>
            Miembro desde {new Date(user.created_at).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '13px' }}>
            {user.dues_paid_until ? (
              new Date(user.dues_paid_until) >= new Date(new Date().toDateString()) ? (
                <span style={{ color: 'var(--success-text)' }}>Anualidad vigente hasta {new Date(user.dues_paid_until).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}</span>
              ) : (
                <span style={{ color: 'var(--danger-text)' }}>Anualidad vencida desde {new Date(user.dues_paid_until).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}</span>
              )
            ) : (
              <span style={{ color: 'var(--neutral-500)' }}>Anualidad sin registrar</span>
            )}
          </p>
          {/* Datos bancarios: visibles para cualquier admin/owner (antes solo owner, cambió
              a petición de Elías Muñoz). Esta pantalla completa ya es admin-only, así que
              basta con que el backend los haya mandado. */}
          {['admin', 'owner'].includes(myRole) && (
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--neutral-500)' }}>
              {user.clabe
                ? `${user.bank_name || 'Banco sin especificar'} · CLABE ${user.clabe}${user.account_holder ? ` · Titular: ${user.account_holder}` : ''}`
                : 'Sin datos bancarios registrados'}
            </p>
          )}
        </div>
        <div className="admin-user-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {user.role === 'owner' && myRole !== 'owner' ? (
            <span style={{ fontSize: '12px', color: 'var(--neutral-500)' }}>Solo un owner puede editar esta cuenta</span>
          ) : (
            <>
              <button className="btn btn-secondary btn-sm" onClick={editProfile}>Editar perfil</button>
              {['pending', 'rejected'].includes(user.status) && myRole === 'owner' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <select value={approveRole} onChange={(e) => setApproveRole(e.target.value)} className="form-input" style={{ padding: '4px 6px', fontSize: '12px', width: 'auto' }}>
                    <option value="member">Miembro (negocio)</option>
                    <option value="admin">Administrador</option>
                    <option value="owner">Dueño</option>
                  </select>
                  <button className="btn btn-success btn-sm" onClick={approveWithRole}>Aprobar</button>
                </span>
              )}
              {['pending', 'rejected'].includes(user.status) && myRole !== 'owner' && (
                <button className="btn btn-success btn-sm" onClick={() => updateUser({ status: 'active' })}>Aprobar</button>
              )}
              {user.status === 'pending' && (
                <button className="btn btn-secondary btn-sm" onClick={() => updateUser({ status: 'rejected' }, `¿Rechazar a ${user.name}? No va a poder usar el sistema.`)}>Rechazar</button>
              )}
              {/* Activo/Inactivo para quien ya era miembro (ver AdminUsers.js), sin correo. */}
              {user.status === 'active' && user.id !== myId && (
                <button className="btn btn-secondary btn-sm" onClick={() => updateUser({ status: 'inactive' }, `¿Desactivar a ${user.name}? No va a poder iniciar sesión. Su historial de referencias y comisiones se conserva y puedes reactivarlo cuando quieras. No se le envía ningún correo.`)}>Desactivar</button>
              )}
              {user.status === 'inactive' && (
                <button className="btn btn-success btn-sm" onClick={() => updateUser({ status: 'active' }, `¿Reactivar a ${user.name}? Va a poder iniciar sesión de nuevo.`)}>Reactivar</button>
              )}
              {myRole === 'owner' && user.id !== myId && user.role === 'member' && (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={() => updateUser({ role: 'admin' }, `¿Dar permisos de administrador a ${user.name}?`)}>Hacer admin</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => updateUser({ role: 'owner' }, `¿Hacer a ${user.name} owner? Va a tener los mismos permisos que tú.`)}>Hacer owner</button>
                </>
              )}
              {myRole === 'owner' && user.id !== myId && user.role === 'admin' && (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={() => updateUser({ role: 'owner' }, `¿Hacer a ${user.name} owner? Va a tener los mismos permisos que tú.`)}>Hacer owner</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => updateUser({ role: 'member' }, `¿Quitarle permisos de administrador a ${user.name}?`)}>Quitar admin</button>
                </>
              )}
              {myRole === 'owner' && user.id !== myId && user.role === 'owner' && (
                <button className="btn btn-secondary btn-sm" onClick={() => updateUser({ role: 'admin' }, `¿Quitarle el rol de owner a ${user.name}? Se queda como admin normal.`)}>Quitar owner</button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="kpi-row" style={{ marginTop: '18px' }}>
        <StatTile label="Referencias enviadas" value={referralsSent.length} />
        <StatTile label="Referencias recibidas" value={referralsReceived.length} />
        <StatTile label="Negocios cerrados" value={dealsClosed} />
        <StatTile label="Comisión ganada" value={money(totalEarned)} />
        <StatTile label="Comisión pagada" value={money(totalPaid)} />
        <StatTile label="Comisión pendiente" value={money(totalPending)} />
        <StatTile label="Invitados registrados" value={invitados.length} />
        <StatTile label="Asistencias a eventos" value={attendance.length} />
      </div>

      <div className="card" style={{ marginTop: '18px' }}>
        <h3 style={{ fontSize: '15px', margin: '0 0 10px' }}>Servicios que ofrece</h3>
        {services.length === 0 ? <p style={{ fontSize: '13px', color: 'var(--neutral-500)' }}>Sin servicios registrados</p> : (
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px' }}>
            {services.map(s => <li key={s.id}>{s.service_name} — {s.commission_percentage}%{s.description ? ` — ${s.description}` : ''}{!s.active ? ' (inactivo)' : ''}</li>)}
          </ul>
        )}
      </div>

      <div className="card" style={{ marginTop: '18px' }}>
        <h3 style={{ fontSize: '15px', margin: '0 0 10px' }}>Referencias enviadas ({referralsSent.length})</h3>
        {referralsSent.length === 0 ? <p style={{ fontSize: '13px', color: 'var(--neutral-500)' }}>Sin referencias enviadas</p> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Folio</th><th>Prospecto</th><th>Empresa</th><th>Estado</th><th>Monto</th></tr></thead>
              <tbody>
                {referralsSent.map(r => (
                  <tr key={r.id}>
                    <td><button className="link-button" onClick={() => setSelectedReferral(r)}>{folio(r.id)}</button></td>
                    <td>{r.prospect_name}</td>
                    <td>{r.provider_business_name}</td>
                    <td><StatusBadge status={r.status} businessName={r.provider_business_name} small /></td>
                    <td className="mono-num">{r.deal_amount ? money(r.deal_amount) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: '18px' }}>
        <h3 style={{ fontSize: '15px', margin: '0 0 10px' }}>Referencias recibidas ({referralsReceived.length})</h3>
        {referralsReceived.length === 0 ? <p style={{ fontSize: '13px', color: 'var(--neutral-500)' }}>Sin referencias recibidas</p> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Folio</th><th>Prospecto</th><th>Referrer</th><th>Estado</th><th>Monto</th></tr></thead>
              <tbody>
                {referralsReceived.map(r => (
                  <tr key={r.id}>
                    <td><button className="link-button" onClick={() => setSelectedReferral(r)}>{folio(r.id)}</button></td>
                    <td>{r.prospect_name}</td>
                    <td>{r.referrer_name}</td>
                    <td><StatusBadge status={r.status} businessName={r.provider_business_name} small /></td>
                    <td className="mono-num">{r.deal_amount ? money(r.deal_amount) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: '18px' }}>
        <h3 style={{ fontSize: '15px', margin: '0 0 10px' }}>Comisiones ganadas ({commissionsEarned.length})</h3>
        {commissionsEarned.length === 0 ? <p style={{ fontSize: '13px', color: 'var(--neutral-500)' }}>Sin comisiones registradas</p> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Folio</th><th>Prospecto</th><th>Monto base</th><th>Su comisión</th><th>Pagada</th></tr></thead>
              <tbody>
                {commissionsEarned.map(c => (
                  <tr key={c.id}>
                    <td>{folio(c.referral_id)}</td>
                    <td>{c.prospect_name}</td>
                    <td className="mono-num">{money(c.base_amount)}</td>
                    <td className="mono-num">{money(c.member_commission)} <span style={{ color: 'var(--neutral-500)', fontWeight: 400 }}>({c.member_percentage}%)</span></td>
                    <td><StatusPill tone={c.paid ? 'success' : 'warning'}>{c.paid ? 'Sí' : 'No'}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: '18px' }}>
        <h3 style={{ fontSize: '15px', margin: '0 0 10px' }}>Asistencia a eventos ({attendance.length})</h3>
        {attendance.length === 0 ? <p style={{ fontSize: '13px', color: 'var(--neutral-500)' }}>Sin asistencias registradas</p> : (
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px' }}>
            {attendance.map(a => (
              <li key={a.id}>{EVENT_TYPE_LABELS[a.event_type] || a.event_type} — {EVENT_MODALITY_LABELS[a.event_modality] || a.event_modality} — {formatEventDate(a.event_date)}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="card" style={{ marginTop: '18px' }}>
        <h3 style={{ fontSize: '15px', margin: '0 0 10px' }}>Invitados llevados ({invitados.length})</h3>
        {invitados.length === 0 ? <p style={{ fontSize: '13px', color: 'var(--neutral-500)' }}>Sin invitados registrados</p> : (
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px' }}>
            {invitados.map(i => (
              <li key={i.id}>{i.name}{i.business_name ? ` (${i.business_name})` : ''} — {INVITADO_STATUS[i.status]?.label || i.status}</li>
            ))}
          </ul>
        )}
      </div>

      <ReferralDetailModal referral={selectedReferral} onClose={() => setSelectedReferral(null)} actions={adminReferralActions(selectedReferral)} />
    </div>
  );
}

export default AdminMemberProfile;
